import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AreaQuantityInputComponent } from '../../../shared/area-quantity-input/area-quantity-input.component';
import { SingleSelectDropdownComponent } from '../../business-details/single-select-dropdown.component';
import { NumericInputDirective } from '../../../shared/directives/numeric-input.directive';
import { CategoryDTO, SupplierDTO, UnitOfMeasureDTO } from '../inventory-dashboard.component';

export interface DropdownOption {
  value: string;
  label: string;
}

/**
 * Shared add/edit item modal (raw material / finished good / service), extracted
 * out of InventoryDashboardComponent's inline template. Owns no HTTP calls and no
 * business logic of its own — the parent still owns `inventoryForm`, `supplierForm`
 * and `categoryForm` (passed in by reference, so reactive-form bindings inside this
 * component read/write the parent's form directly) plus `saveItem()`/`createSupplier()`/
 * `createCategory()`. This component is purely presentational glue: it renders the
 * form and re-emits user actions for the parent to handle, exactly as the inline
 * template's (click) handlers did.
 */
@Component({
  selector: 'app-inventory-item-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    AreaQuantityInputComponent,
    SingleSelectDropdownComponent,
    NumericInputDirective,
  ],
  templateUrl: './inventory-item-modal.component.html',
  styleUrls: ['./inventory-item-modal.component.css'],
})
export class InventoryItemModalComponent {
  readonly String = String;

  // Visibility / mode
  @Input() visible = false;
  @Input() isEditing = false;
  @Input() isFinishedGood = false;
  @Input() isServiceItem = false;
  @Input() isAddingQuantity = false;
  @Input() showPurchaseFields = false;

  // Forms (shared FormGroup instances owned by the parent)
  @Input() inventoryForm!: FormGroup;
  @Input() supplierForm!: FormGroup;
  @Input() categoryForm!: FormGroup;

  // Supplier / category selection state
  @Input() isCreatingSupplier = false;
  @Output() isCreatingSupplierChange = new EventEmitter<boolean>();

  @Input() isCreatingCategory = false;
  @Output() isCreatingCategoryChange = new EventEmitter<boolean>();

  @Input() selectedSupplier: SupplierDTO | null = null;
  @Input() selectedCategory: CategoryDTO | null = null;

  @Input() supplierOptions: DropdownOption[] = [];
  @Input() categoryOptions: DropdownOption[] = [];
  @Input() uomList: UnitOfMeasureDTO[] = [];

  // Actions
  @Output() save = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<void>();
  /** Header "x" button — mirrors the inline template's inline reset-and-close, distinct from the footer Cancel button's closeAddModal(). */
  @Output() quickClose = new EventEmitter<void>();
  @Output() createSupplierClick = new EventEmitter<void>();
  @Output() createCategoryClick = new EventEmitter<void>();
  @Output() navigateToAddSupplierClick = new EventEmitter<void>();
  @Output() togglePurchaseFieldsClick = new EventEmitter<void>();
  @Output() supplierSelected = new EventEmitter<string | null>();
  @Output() categorySelected = new EventEmitter<string | null>();
  @Output() quantityToAddChange = new EventEmitter<number>();

  compareUOM(uom1: any, uom2: any): boolean {
    const id1 = typeof uom1 === 'object' ? uom1?.uomid : uom1;
    const id2 = typeof uom2 === 'object' ? uom2?.uomid : uom2;
    return id1 === id2;
  }

  getSelectedUomCode(): string {
    const id = this.inventoryForm?.get('PurchaseUOMID')?.value;
    if (!id) return '';
    return this.uomList.find(u => u.uomid === id)?.uomCode ?? '';
  }

  /**
   * A Service is quantified as time, so its unit is fixed to Minutes rather
   * than being chosen — the user enters how many minutes the service takes.
   */
  private static readonly MINUTES_UOM_CODES = ['min', 'minute', 'minutes'];

  get minutesUom(): UnitOfMeasureDTO | null {
    const codes = InventoryItemModalComponent.MINUTES_UOM_CODES;
    return (
      this.uomList.find(u => codes.includes((u.uomCode ?? '').trim().toLowerCase())) ??
      this.uomList.find(u => (u.uomName ?? '').trim().toLowerCase() === 'minutes') ??
      null
    );
  }

  /** Units offered in the dropdown — a Service is locked to Minutes. */
  get displayUomList(): UnitOfMeasureDTO[] {
    if (!this.isServiceItem) return this.uomList;
    const minutes = this.minutesUom;
    return minutes ? [minutes] : this.uomList;
  }

  /**
   * Pins a Service to the Minutes unit as soon as the unit list is available.
   * Called from the template so it also covers the case where the units load
   * after the dialog has already been opened.
   */
  get serviceUomLocked(): boolean {
    if (!this.isServiceItem) return false;
    const minutes = this.minutesUom;
    if (!minutes) return false;
    const control = this.inventoryForm?.get('PurchaseUOMID');
    if (control && control.value !== minutes.uomid) {
      // Defer so the write lands outside the current change-detection pass.
      Promise.resolve().then(() => control.setValue(minutes.uomid));
    }
    return true;
  }

  get purchasePriceError(): string | null {
    const control = this.inventoryForm?.get('purchasePrice');
    if (!control || !control.touched) return null;
    if (control.errors?.['invalidPrice']) return control.errors['invalidPrice'];
    if (control.errors?.['min']) return 'Price cannot be negative';
    if (control.errors?.['required']) return 'Purchase price is required';
    return null;
  }

  /**
   * Inline validation message for a form control. The form itself already
   * carried validators, but nothing rendered their state, so a rejected save
   * gave the user no indication of which field was at fault.
   */
  errorFor(controlName: string, label: string): string | null {
    const control = this.inventoryForm?.get(controlName);
    if (!control || !control.touched) return null;
    if (control.errors?.['required']) return `${label} is required`;
    if (control.errors?.['min']) return `${label} cannot be negative`;
    if (control.errors?.['max']) return `${label} is above the allowed maximum`;
    if (control.errors?.['invalidPrice']) return control.errors['invalidPrice'];
    return null;
  }

  isInvalid(controlName: string): boolean {
    const control = this.inventoryForm?.get(controlName);
    return !!control && control.touched && control.invalid;
  }

  /**
   * Touch every control before handing off to the parent, so any field the
   * parent's own guards reject is highlighted with its message rather than
   * only surfacing as a generic alert.
   */
  onSaveClick(): void {
    this.inventoryForm?.markAllAsTouched();
    this.save.emit();
  }

  onSelectExistingSupplier(): void {
    this.isCreatingSupplier = false;
    this.isCreatingSupplierChange.emit(false);
  }

  onSelectExistingCategory(): void {
    this.isCreatingCategory = false;
    this.isCreatingCategoryChange.emit(false);
  }

  onCreateCategoryTab(): void {
    this.isCreatingCategory = true;
    this.isCreatingCategoryChange.emit(true);
  }

  onDesktopSupplierSelected(idStr: string | null): void {
    this.supplierSelected.emit(idStr);
  }

  onDesktopCategorySelected(idStr: string | null): void {
    this.categorySelected.emit(idStr);
  }

  setQuantityToAdd(val: number): void {
    this.quantityToAddChange.emit(val);
  }
}
